<i18n>
  {
    "en": {
      "availableData": "There are no child locations",
      "dictionaryItemSelector": {
        "parent": "Higher location",
        "parentPlaceholder": "Select higher location"
      },
      "agent": "Agent",
      "totalCnt": "Total",
      "status": "Status",
      "okCnt": "OK",
      "warningCnt": "Warning",
      "errorCnt": "Error"
    },
    "ru": {
      "availableData": "Дочерних локаций нет",
      "dictionaryItemSelector": {
        "parent": "Вышестоящая локация",
        "parentPlaceholder": "Выберите вышестоящую локацию"
      },
      "agent": "Агент",
      "totalCnt": "Всего",
      "status": "Статус",
      "okCnt": "Ок",
      "warningCnt": "Предупреждение",
      "errorCnt": "Ошибка"
    }
  }
</i18n>

<template>
  <div class="card-location" :class="{ 'no-gap': isNew }">
    <div v-if="dataSource.location" class="info-section">
      <div class="properties outlined-box-lg">
        <div class="section-title section-title-mb-6">{{$t('locations.dictionaryItem.property')}}</div>

        <DxTextBox
          class="dx-field-value"
          labelMode="static"
          :label="$t('locations.dictionaryItem.name')"
          width="100%"
          :readOnly="!canEditLocation"
          :disabled="loading"
          :hint="dataSource.location.name"
          v-model="dataSource.location.name"
        >
          <DxValidator>
            <DxRequiredRule :message="$t('general.mandatory')" />
          </DxValidator>
        </DxTextBox>

        <dictionary-item-selector
          v-if="dataSource.location.code !== 'DEFAULT'"
          :readOnly="!canEditLocation"
          :disabled="loading"
          :items="locationsItems"
          :label="$t('dictionaryItemSelector.parent')"
          :placeholder="$t('dictionaryItemSelector.parentPlaceholder')"
          v-model="dataSource.location.parent"
        />

        <DxTextArea
          class="dx-field-value"
          :minHeight="55"
          width="100%"
          :readOnly="!canEditLocation"
          :disabled="loading"
          labelMode="static"
          :label="$t('locations.dictionaryItem.description')"
          v-model="dataSource.attributes['__DESCRIPTION'].stringValue"
        />
      </div>

      <div class="address outlined-box-lg">
        <div class="section-title section-title-mb-6">{{$t('locations.dictionaryItem.address')}}</div>

        <div class="address-row">
          <DxTextBox
            class="dx-field-value"
            labelMode="static"
            :label="$t('locations.dictionaryItem.index')"
            width="calc(30% - 6px)"
            :readOnly="!canEditLocation"
            :disabled="loading"
            :hint="dataSource.attributes['__ADDRESS_INDEX'].stringValue"
            v-model="dataSource.attributes['__ADDRESS_INDEX'].stringValue"
          />

          <DxTextBox
            class="dx-field-value"
            labelMode="static"
            :label="$t('locations.dictionaryItem.city')"
            width="calc(70% - 6px)"
            :readOnly="!canEditLocation"
            :disabled="loading"
            :hint="dataSource.attributes['__ADDRESS_CITY'].stringValue"
            v-model="dataSource.attributes['__ADDRESS_CITY'].stringValue"
          />
        </div>

        <div class="address-row">
          <DxTextBox
            class="dx-field-value"
            labelMode="static"
            :label="$t('locations.dictionaryItem.street')"
            width="calc(70% - 6px)"
            :readOnly="!canEditLocation"
            :disabled="loading"
            :hint="dataSource.attributes['__ADDRESS_STREET'].stringValue"
            v-model="dataSource.attributes['__ADDRESS_STREET'].stringValue"
          />

          <DxTextBox
            class="dx-field-value"
            labelMode="static"
            :label="$t('locations.dictionaryItem.house')"
            width="calc(30% - 6px)"
            :readOnly="!canEditLocation"
            :disabled="loading"
            :hint="dataSource.attributes['__ADDRESS_HOUSE'].stringValue"
            v-model="dataSource.attributes['__ADDRESS_HOUSE'].stringValue"
          />
        </div>

        <div class="address-row">
          <DxTextBox
            class="dx-field-value"
            labelMode="static"
            :label="$t('locations.dictionaryItem.floor')"
            width="calc(30% - 6px)"
            :readOnly="!canEditLocation"
            :disabled="loading"
            :hint="dataSource.attributes['__ADDRESS_FLOOR'].stringValue"
            v-model="dataSource.attributes['__ADDRESS_FLOOR'].stringValue"
          />

          <DxTextBox
            class="dx-field-value"
            labelMode="static"
            :label="$t('locations.dictionaryItem.room')"
            width="calc(70% - 6px)"
            :readOnly="!canEditLocation"
            :disabled="loading"
            :hint="dataSource.attributes['__ADDRESS_ROOM'].stringValue"
            v-model="dataSource.attributes['__ADDRESS_ROOM'].stringValue"
          />
        </div>
      </div>

      <div class="contact-person outlined-box-lg">
        <div class="section-title section-title-mb-6">{{$t('locations.dictionaryItem.person')}}</div>

        <DxTextBox
          class="dx-field-value"
          labelMode="static"
          :label="$t('locations.dictionaryItem.fio')"
          width="100%"
          :readOnly="!canEditLocation"
          :disabled="loading"
          :hint="dataSource.attributes['__CONTACT_NAME'].stringValue"
          v-model="dataSource.attributes['__CONTACT_NAME'].stringValue"
        />

        <DxTextBox
          class="dx-field-value"
          labelMode="static"
          :label="$t('locations.dictionaryItem.position')"
          width="100%"
          :readOnly="!canEditLocation"
          :disabled="loading"
          :hint="dataSource.attributes['__CONTACT_POSITION'].stringValue"
          v-model="dataSource.attributes['__CONTACT_POSITION'].stringValue"
        />

        <DxTextBox
          class="dx-field-value"
          labelMode="static"
          :label="$t('locations.dictionaryItem.telephones')"
          width="100%"
          :readOnly="!canEditLocation"
          :disabled="loading"
          :hint="dataSource.attributes['__CONTACT_PHONES'].stringValue"
          v-model="dataSource.attributes['__CONTACT_PHONES'].stringValue"
        />
      </div>
    </div>

    <div class="system-details">
      <div v-if="!isNew" class="locations outlined-box-lg">
        <div class="section-title">{{$t('locations.dictionaryItem.location')}}</div>

        <div class="outlined-box">
          <span v-if="!locationDictionaryChild || locationDictionaryChild.length === 0 && !loading" class="available-locations">{{ $t('availableData') }}</span>

          <DxScrollView
            v-if="locationDictionaryChild.length > 0 && !loading"
            :scroll-by-content="true"
            :scroll-by-thumb="true"            
            show-scrollbar="onHover"
            :height="260"            
          >
            <div v-for="(location, index) in locationDictionaryChild" :key="index" class="location-item">
              <router-link class="link" :to="`/location/${location.id}`">{{ location.name }}</router-link>
            </div>
          </DxScrollView>
        </div>
      </div>

      <div class="group">
        <div
          v-if="!isNew"
          class="devices outlined-box-lg"
        >
          <div class="section-title">{{$t('locations.dictionaryItem.devices')}}</div>

          <div class="outlined-box">
            <ul v-if="!loading" class="devices-list">
              <li class="devices-item">
                <div class="line">
                  <span class="label">{{$t('totalCnt')}}:</span>
                  <router-link class="link" :to="{ name: 'devices', params: { locationId: dataSource.location.id }}">{{ statisticsDevices.totalCnt }}</router-link>
                </div>
              </li>

              <li v-if="statisticsDevices.okCnt > 0" class="devices-item">
                <div class="line">
                  <span class="label">{{$t('status')}}
                    <span class="ok">{{$t('okCnt')}}</span> :
                  </span>
                  <router-link class="link" :to="{ name: 'devices', params: { locationId: dataSource.location.id, deviceStatusId: printerStatusCodes['OK'].id }}">{{ statisticsDevices.okCnt }}</router-link>
                </div>
              </li>

              <li v-if="statisticsDevices.warningCnt > 0" class="devices-item">
                <div class="line">
                  <span class="label">{{$t('status')}}
                    <span class="warning">{{$t('warningCnt')}}</span>:
                  </span>
                  <router-link class="link" :to="{ name: 'devices', params: { locationId: dataSource.location.id, deviceStatusId: printerStatusCodes['WARNING'].id }}">{{ statisticsDevices.warningCnt }}</router-link>
                </div>
              </li>

              <li v-if="statisticsDevices.errorCnt > 0" class="devices-item">
                <div class="line">
                  <span class="label">{{$t('status')}}
                    <span class="error">{{$t('errorCnt')}}</span>:
                  </span>
                  <router-link class="link" :to="{ name: 'devices', params: { locationId: dataSource.location.id, deviceStatusId: printerStatusCodes['ERROR'].id }}">{{ statisticsDevices.errorCnt }}</router-link>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div
          v-if="!isNew && hasLicenses"
          class="licenses outlined-box-lg"
        >
          <div class="section-title">{{$t('menu.licenses')}}</div>

          <div v-if="!loading" class="outlined-box">
            <ul class="licenses-list">
              <li v-for="(license, index) in licenseItems" :key="index" class="license-item">
                <router-link v-if="dataSource.location.code === 'DEFAULT'" :class="getClassLicense(license)" :to="{ name: 'license', params: { id: dataSource.location.code } }">
                  {{formatLicenseData(license.dateFrom)}}-{{formatLicenseData(license.dateTo)}}: {{license.deviceCount}}
                </router-link>
                <router-link v-else :class="getClassLicense(license)" :to="{ name: 'license', params: { id: license.id } }">
                  {{formatLicenseData(license.dateFrom)}}-{{formatLicenseData(license.dateTo)}}: {{license.deviceCount}}
                </router-link>
              </li>
            </ul>
            <router-link class="link" :to="{ name: 'license', params: { id: dataSource.location.code } }">
              {{$t('general.add')}}
            </router-link>
          </div>
        </div>

        <div v-if="hasAgents" class="agents outlined-box-lg">
          <div class="section-title">{{$t('locations.dictionaryItem.agents')}}</div>

          <div class="outlined-box">
            <div v-if="agentsItems.length === 1">
              <router-link class="link" :to="{ name: 'agent', params: { id: agentsItems[0].id }}">
                {{agentsItems[0].location.id === locationId ? $t('agent') : `${agentsItems[0].location.name}-${$t('agent')}`}}
              </router-link>
            </div>

            <ul v-else class="agents-list">
              <li v-for="(agent, index) in agentsItems" :key="index" class="agent-item">
                <span v-if="agent.location.id">
                  <router-link class="link" :to="{ name: 'agent', params: { id: agent.id }}">
                    {{agent.location.id === locationId ? `${$t('agent')}${index+1}` : `${agent.location.name}-${$t('agent')}`}}
                  </router-link>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapCacheActions } from "vuex-cache";

import baseView from '../base.vue';

import { DxValidator, DxRequiredRule } from 'devextreme-vue/validator';
import DxTextBox from 'devextreme-vue/text-box';
import DxTextArea from 'devextreme-vue/text-area';
import { DxScrollView } from 'devextreme-vue/scroll-view';

import DictionaryItemSelector from './dictionary-item-selector';
import { DataLocation } from "@/mixins/dataLocationMixin";

import { userLicenses, userLocationsManagedLicense, getLicense } from '@/services/api';
import { AclHandler } from "@/services/acl-handler";

import { findIndex } from 'lodash';
import moment from "moment";


export default {
  name: 'LocationDetail',
  props: {
    locationId: {
      type: Number,
      required: true
    },
    dataSource: {
      type: Object,
      required: true
    },
    locationsItems: {
      type: Array,
      required: true
    },
    agentsItems: {
      type: Array,
      required: true
    },
    locationDictionaryChild: {
      type: Array,
      required: true
    },
    statisticsDevices: {
      type: Object,
      required: true
    }
  },
  extends: baseView,
  mixins: [DataLocation],
  components: {
    DxRequiredRule, DictionaryItemSelector, DxTextBox, DxTextArea, DxValidator, DxScrollView
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      colCountByScreen: {
        xs: 1,
        sm: 1,
        md: 3,
        lg: 3
      },
      licenseItems: [],
      defaultLicense: {},
      managedLicense: {},
      userLicenses: [],
      printerStatusCodes: [],
      loading: true
    };
  },
  computed: {
    isNew() {
      return this.locationId < 0;
    },
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    isAdmin() {
      return this.aclUser.hasPermission({ roleCodes : ['ADMIN', 'DEVELOPER'] });
    },
    isDefaultAdmin() {
      return this.aclUser.hasPermission({ roleCodes : ['ADMIN', 'DEVELOPER'] }) && this.aclUser.hasDefaultLocation();
    },
    canEditLocation() {
      return this.aclUser.hasGrantForOperation('PAGE_LOCATION', 'EDIT') || false;
    },
    isCreateLicense() {
      return this.defaultLicense.status !== 'ERROR' ||
        this.managedLicense.find(l => l.code === this.dataSource.location.code) ||
        this.userLicenses.length > 0;
    },
    hasLicenses() {
      return this.isCreateLicense && this.isDefaultAdmin;
    },
    hasAgents() {
      return this.isAdmin && this.agentsItems.length > 0;
    }
  },
  methods: {
    ...mapCacheActions(["getPrinterStatusCodes"]),
    isTodayInRange(license) {
      const time = moment();
      const beforeTime = moment(license.dateFrom);
      const afterTime = moment(license.dateTo);

      return time.isBetween(beforeTime, afterTime);
    },
    getClassLicense(license) {
      let className = 'link';
      if (this.isTodayInRange(license))
        return className + ' bold';
      else
        return className;
    },
    formatLicenseData(date) {
      return moment(date).format('DD.MM.YYYY');
    },
    async loadData() {
      try {
        this.$emit('loading', true);

        this.locations = this.locationsItems;

        if (!this.isNew) {
          const statusCodes = await this.getPrinterStatusCodes();
          statusCodes.forEach(item => {
            this.printerStatusCodes[item.code] = item;
          });

          if (this.dataSource.location.code === 'DEFAULT') {
            this.defaultLicense = await getLicense();
            if (this.defaultLicense.status !== 'ERROR') {
              this.defaultLicense.dateFrom = this.defaultLicense.from;
              this.defaultLicense.dateTo = this.defaultLicense.to;
              delete this.defaultLicense.from;
              delete this.defaultLicense.to;
              this.licenseItems = [this.defaultLicense];
            } else {
              this.licenseItems = [];
            }
          } else {
            this.managedLicense = await userLocationsManagedLicense();

            const idCurrentLocation = this.locationId;
            const indexHigherLocation = findIndex(this.locationsItems, ['id', idCurrentLocation]) - 1;
            const idHigherLocation = indexHigherLocation > 0 ? this.locationsItems[indexHigherLocation].id : indexHigherLocation;
 
            let filter = [];
            if (idHigherLocation !== -1)
              filter = [['location.id', '=', idCurrentLocation], 'or', ['location.id', '=', idHigherLocation]];
            else
              filter = ['location.id', '=', idCurrentLocation];

            const response = await userLicenses({
              filterJson: filter,
              page: '999,1',
              sort: ['dateFrom,ASC']
            });
            this.userLicenses = response.pageRows;
            this.licenseItems = this.userLicenses.filter(license => license.location.id !== idHigherLocation);
          }
        }
      } catch(err) {
        this.onDataError(err);
      } finally {
        this.loading = false;
        this.$emit('loading', false);
      }
    },
  },
  created() {
  },
  mounted() {
    // this.loadData();
  },
  watch: {
    'dataSource.location.code'() {
      this.loadData();
    },
    locationId() {
      this.licenseItems = [];
    }
  }
};
</script>

<style lang="scss">
.no-gap {
  gap: 0 !important;
}

.available-locations {
  color: #999;
  font-size: 17px;
}

.section-title-mb-6 {
  margin-bottom: 6.5px !important;
}

.section-title {
  font-weight: 600;
  margin-bottom: 14px;
  font-size: 16px;
}

.outlined-box-lg {
  padding: 14px;
  border-radius: 10px;
  border: 1px solid #20304A;
}

.outlined-box {
  height: 100%;
  border-radius: 4px;
  border: 1px solid #B6BAC2;
  padding: 6px 12px;
}

.card-location {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .info-section {
    display: flex;
    flex-direction: row;
    column-gap: 12px;

    .properties {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      flex: 1 1;
    }

    .address {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      flex: 1 1;

      .address-row {
        display: flex;
        flex-direction: row;
        column-gap: 7px;
      }
    }

    .contact-person {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      flex: 1 1;
    }
  }

  .system-details {
    display: flex;
    flex-direction: row;
    column-gap: 12px;

    .locations {
      display: flex;
      flex-direction: column;
      flex: 0 0 calc((100% - 24px) / 3); // 3 колонки => 2 gap'а = 24px, делим на 3

      .location-item {
        margin-bottom: 8px;
      }
    }

    .group {
      display: flex;
      gap: 12px;
      flex: 1; // занимать оставшееся место

      .devices {
        display: flex;
        flex-direction: column;
        flex: 1 1;

        .devices-list {
          list-style: none;
          padding: 0;
          margin: 0;

          .devices-item {
            width: 100%;
            margin-bottom: 8px;

            .line {
              display: flex;
              // justify-content: space-between;
              align-items: center;
              width: 100%;

              .label {
                flex-shrink: 1;
                margin-right: 8px;
                white-space: nowrap;
              }

              .ok {
                font-weight: 600;
                color: rgb(0,128,0);
              }

              .warning {
                font-weight: 600;
                color: rgb(255,165,0);
              }

              .error {
                font-weight: 600;
                color: rgb(255,0,0);
              }

              .not-serviced {
                font-weight: 600;
                color: rgb(170,170,170);
              }
            }

            .link {
              font-weight: 600;
            }
          }
        }
      }

      .licenses {
        display: flex;
        flex-direction: column;
        flex: 1 1;

        .licenses-list {
          columns: 1;
          list-style: none;
          padding: 0;
          margin: 0;

          .license-item {
            margin-bottom: 8px;
          }

          .bold {
            font-weight: 700;
          }
        }
      }

      .agents {
        display: flex;
        flex-direction: column;
        flex: 1 1;

        .agents-list {
          list-style: none;
          padding: 0;
          margin: 0;

          .agent-item {
            width: 100%;
            margin-bottom: 8px;
          }
        }
      }
    }
  }
}
</style>
